<script setup lang="ts">
import { ref } from 'vue'

import PathwayLandingBrandMark from './PathwayLandingBrandMark.vue'

interface FooterCol {
  title: string
  links: string[]
}

const footerCols: FooterCol[] = [
  {
    title: 'Konfigurator',
    links: [
      'Pojazdy dostawcze',
      'Pojazdy ciężarowe',
      'Naczepy',
      'Pojazdy specjalne',
      'Kalkulator doboru',
    ],
  },
  {
    title: 'Wsparcie',
    links: [
      'Serwis pogwarancyjny',
      'Instrukcje obsługi',
      'Części zamienne',
      'Szkolenia kierowców',
      'Zgłoszenie usterki',
    ],
  },
]

const certs = ['ISO\n9001', 'ECE\nR29', 'ATP\nFRC', 'CE']

const openCols = ref<Set<string>>(new Set())

function toggle(title: string): void {
  if (openCols.value.has(title)) {
    openCols.value.delete(title)
  } else {
    openCols.value.add(title)
  }
}
</script>

<template>
  <footer
    class="bg-pw-ink text-pw-paper relative px-5 pt-12 pb-8 md:px-14 md:pt-20"
  >
    <div class="mx-auto max-w-[1440px]">
      <div class="md:border-pw-line pb-5 md:mb-5 md:border-b">
        <a
          href="#"
          class="text-pw-paper inline-flex items-center gap-2.5 text-[22px] font-extrabold tracking-tight no-underline"
        >
          <PathwayLandingBrandMark />
          <span>FLEX</span>
        </a>
        <p class="text-pw-muted my-4 max-w-[320px] text-[14px] leading-[1.55]">
          Konfigurator i producent zabudów do pojazdów użytkowych. Realizujemy
          zlecenia dla flot logistycznych, sieci handlowych i firm
          specjalistycznych w całej Europie.
        </p>
        <div class="flex gap-3">
          <span
            v-for="c in certs"
            :key="c"
            class="border-pw-line text-pw-muted hover:border-pw-yellow hover:text-pw-orange grid h-12 w-12 place-items-center border text-center font-mono text-[9px] leading-[1.1] font-bold tracking-[0.06em] whitespace-pre transition-colors md:h-14 md:w-14"
          >
            {{ c }}
          </span>
        </div>
      </div>

      <div
        class="footer-grid border-pw-line hidden gap-14 border-b pb-14 md:grid"
      >
        <div></div>

        <div v-for="col in footerCols" :key="col.title">
          <h5
            class="text-pw-muted m-0 mb-4 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase"
          >
            {{ col.title }}
          </h5>
          <ul class="m-0 flex list-none flex-col gap-2.5 p-0">
            <li v-for="link in col.links" :key="link">
              <a
                href="#"
                class="text-pw-paper hover:text-pw-orange text-[14px] no-underline transition-colors"
                >{{ link }}</a
              >
            </li>
          </ul>
        </div>

        <div id="contact">
          <h5
            class="text-pw-muted m-0 mb-4 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase"
          >
            Kontakt
          </h5>
          <div class="text-[14px] leading-[1.6]">
            <b class="mb-2 block font-semibold">FLEX Sp. z o.o.</b>
            ul. Testowa 67<br />30-003 Kraków<br /><br />
            <b class="text-pw-orange mb-2 block font-semibold"
              >+48 22 723 31 57</b
            >
            <a href="#" class="text-pw-paper no-underline"
              >biuro@flex-konfigurator.pl</a
            >
          </div>
        </div>
      </div>

      <div class="border-pw-line flex flex-col border-b md:hidden">
        <div
          v-for="col in footerCols"
          :key="col.title"
          class="border-pw-line border-t"
        >
          <button
            class="text-pw-muted flex w-full cursor-pointer items-center justify-between py-4 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase"
            @click="toggle(col.title)"
          >
            {{ col.title }}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="shrink-0 transition-transform duration-200"
              :class="openCols.has(col.title) ? 'rotate-180' : ''"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <Transition name="accordion">
            <ul
              v-if="openCols.has(col.title)"
              class="m-0 flex list-none flex-col gap-3 p-0 pb-4"
            >
              <li v-for="link in col.links" :key="link">
                <a
                  href="#"
                  class="text-pw-paper hover:text-pw-orange text-[14px] no-underline transition-colors"
                  >{{ link }}</a
                >
              </li>
            </ul>
          </Transition>
        </div>

        <div
          id="contact"
          class="border-pw-line border-t py-6 text-[14px] leading-[1.6]"
        >
          <h5
            class="text-pw-muted m-0 mb-3 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase"
          >
            Kontakt
          </h5>
          <b class="text-pw-orange mb-1 block font-semibold"
            >+48 22 723 31 57</b
          >
          <a href="#" class="text-pw-paper no-underline"
            >biuro@flex-konfigurator.pl</a
          >
        </div>
      </div>

      <div
        class="text-pw-muted flex items-center justify-between pt-6 font-mono text-[11px] tracking-[0.06em]"
      >
        <span>© 2026 FLEX Sp. z o.o. · NIP XXXXXXXXXX · KRS XXXXXXX</span>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer-grid {
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
}

@media (max-width: 1100px) {
  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.accordion-enter-active,
.accordion-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
